/*
 *  This document is the CONFIDENTIAL PROPERTY of Corero Network Security, Inc.
 *  Copyright (C) 2022-2026 by Corero Network Security, Inc. All rights reserved.
 *  Notice of copyright on this document does not constitute publication.
 */

import Flush from '../core/flush';
import _ from 'lodash';
import { jwtDecode } from "jwt-decode";
import Notify from '@corero/ssp-ui-components/core/Notify';

import {requester} from '../core/requester';
import sharedConfig from '../core/sharedConfig';
import core from '../core/core';

const TIME_PADDING = 60;
let user = {};
let tenant = {};
let csrfTokenRefreshIntervalId = null;
let currentStatus = {};
let authenticatedUser = null;

const methods = {

    setCsrfTokenRefreshInterval(expiryInSeconds, timePadding = 0) {
        return !isNaN(Number(expiryInSeconds)) && setInterval(() => this.checkAndRefreshCsrfToken.call(this), (Number(expiryInSeconds) - timePadding) * 1000);
    },

    login(data) {
        requester.post(sharedConfig.apis.login, data, {}, { logoutOnUnauthorised: false })
            .then((res) => {
                localStorage.clear();
                // Use the real username that is returned in the 'sub' claim of the CSRF JWT
                const jwtSub = this.getJwtSub(res.csrfToken);
                //Storing in local storage signifies authentication
                core.saveLocal('authenticatedEmail', jwtSub);
                authenticatedUser = jwtSub
                core.saveLocal('csrfToken', res.csrfToken);
                core.saveLocal('csrfExpiry', res.csrfExpiry);
                core.saveLocal('hideNotification', false);
                currentStatus = { success: true, message: 'Successfully logged in'};
                csrfTokenRefreshIntervalId = this.setCsrfTokenRefreshInterval(res.csrfExpiry, TIME_PADDING);
                this.fetchAuthenticatedUser();
            })
            .catch((err) => {
                let errorMessage = 'Invalid username or password'
                if (err.response.status === 403 && err.response.body.detail.includes('Too many failed password attempts')) {
                    errorMessage = '';
                    setTimeout(() => {
                        // eslint-disable-next-line no-restricted-globals
                        location.reload();
                    }, 3000);
                }
                currentStatus = { success: false, message: errorMessage};
                this.changed();
            })
    },

    logout() {
        requester.post(sharedConfig.apis.logout, {})
            .finally(() => {
                user = {};
                tenant = {};
                clearInterval(csrfTokenRefreshIntervalId);
                localStorage.clear();
                setTimeout(() => {
                    // Ensuring we also clear the WSGI tokens by redirecting to the WSGI logout
                    window.location.href = window.location.origin + '/log_out';
                    }, 100);
            });
    },

    fetchAuthenticatedUser() {
        if (this.isAuth()) {
            requester.get(sharedConfig.apis.userBy, {username: core.getLocal('authenticatedEmail')})
                .then((userData) => {
                    this.setUserAndTenant(userData);
                });
        }
    },

    checkAndRefreshCsrfToken(silent=false) {
        if (this.isAuth()) {
            // empty function is the custom error handler for the requester. if we get an error here we want to return to the login page and not report any error
            return requester.post(sharedConfig.apis.tokenRefresh, {csrfToken: core.getLocal('csrfToken')}, {}, {}, silent, () => {
            })
                .then((res) => {
                    //update local storage
                    authenticatedUser = core.getLocal('authenticatedEmail');
                    core.saveLocal('csrfToken', res.csrfToken);
                    core.saveLocal('csrfExpiry', res.csrfExpiry);
                    //update user
                    clearInterval(csrfTokenRefreshIntervalId);
                    csrfTokenRefreshIntervalId = this.setCsrfTokenRefreshInterval(res.csrfExpiry, TIME_PADDING);
                });
        } else if (!core.getLocal('authenticatedEmail') && authenticatedUser) {
            // If we have been logged out, but we were logged in, logout
            this.logout();
        }
    },

    user() {
        return user;
    },

    tenant() {
        return tenant;
    },

    isAuth() { //Note this checks if authentication was done through login page not basic authentication
        return core.getLocal('authenticatedEmail');
    },

    isSupportUser() {
        return user && user.role === 'supportUser';
    },

    isMspAdmin() {
        return user && user.role === 'mspAdmin';
    },

    isMspOrSupport() {
        return user && ['supportUser', 'mspAdmin', 'mspUser'].includes(user.role);
    },

    isTenant() {
        return user && ['tenantUser', 'tenantAdmin'].includes(user.role);
    },

    isTenantAdmin() {
        return user && user.role === 'tenantAdmin';
    },

    isTenantUser() {
        return user && user.role === 'tenantUser'
    },

    setUserAndTenant(data) {
        _.assign(user, data);
        const roles = _.invert(sharedConfig.roles);
        user.beRole = user.role;
        user.role = roles[user.beRole];

        if (data.tenantId) {
            requester.get(`${sharedConfig.apis.tenants}/${data.tenantId}`)
                .then(data => {
                    _.assign(tenant, data);
                    this.changed();
                })
                .catch(function (err) {
                    return err;
                });
        } else {
            this.changed();
        }
    },

    changePassword(data) {
        return requester.patch(sharedConfig.apis.changePassword, data);
    },

    sendPasswordResetEmail({email, callback}) {
        // don't show errors to unauthenticated user.
        requester.patch(sharedConfig.apis.sendPasswordResetEmail, {}, {email}, {}, {error: false})
            .finally((res) => {
              core.saveLocal('passwordResetUser', email);
              Notify.info("If you are a registered user we have sent you an email containing your password reset token");
              callback();
            })
    },

    validatePasswordResetToken(token, navigate) {
        requester.get(sharedConfig.apis.validatePasswordResetToken, {token})
            .then(() => {
                core.saveSessionProperty('token', token);
                navigate('/login/forgot-password-2');
            });
    },

    resetPassword(newPassword) {
        const token = core.getSessionProperty('token');
        return requester.patch(sharedConfig.apis.resetPassword, {token, newPassword})
            .then(() => {
                Notify.info('Your password has been updated');
            });
    },

    getCurrentStatus() {
        return currentStatus;
    },

    /**
     * Logs in using SSO
     * Attempts to authenticate against the WSGI endpoint as well, so a PUBTKT cookie is generated
     * @param username Username
     * @param csrfToken CSRF Token
     * @param csrfExpiry CSRF Expiry
     */
    ssoLogin({username, csrfToken, csrfExpiry}) {
        const data = { username, csrfToken }
        requester.post(sharedConfig.apis.ssoWsgi, data, {}, {logoutOnUnauthorised: false, error: false})
            .then(() => {
                core.saveLocal('authenticatedEmail', username);
                authenticatedUser = username
                core.saveLocal('csrfToken', csrfToken);
                core.saveLocal('csrfExpiry', csrfExpiry);
                core.saveLocal('hideNotification', false);
                currentStatus = {success: true, message: 'Successfully logged in'};
                csrfTokenRefreshIntervalId = this.setCsrfTokenRefreshInterval(csrfExpiry, TIME_PADDING);
                this.fetchAuthenticatedUser();
            })
            .catch(() => {
                currentStatus = { success: false };
                this.changed();
            })
    },

    getJwtSub(jwt) {
        if (!jwt) {
            return;
        }
        const decodedPayload = jwtDecode(jwt);
        return decodedPayload.sub;
    }
};

const { actions, store } = Flush.store(methods);

export { actions, store };
